<template>
  <div v-if="items.length" class="fls">
    <div class="fls-head">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z"/></svg>
      <span class="fls-head__title">{{ $t('propFaqBooking.faqSuggestions.title') }}</span>
      <span class="fls-head__count">{{ items.length }}</span>
    </div>
    <p class="fls-head__sub">
      {{ $t('propFaqBooking.faqSuggestions.intro') }}
    </p>

    <div class="fls-card" v-for="item in items" :key="item.id">
      <div v-if="global && item.property_name" class="fls-card__prop">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
        {{ item.property_name }}
      </div>
      <div class="fls-card__asked">
        <span class="fls-card__asked-label">{{ $t('propFaqBooking.faqSuggestions.askedLabel') }}</span>
        <span class="fls-card__asked-text">{{ item.question }}</span>
      </div>

      <label class="fls-field">
        <span class="fls-field__label">{{ $t('propFaqBooking.faqSuggestions.questionLabel') }}</span>
        <input v-model="item.q" type="text" class="fls-input" :placeholder="$t('propFaqBooking.faqSuggestions.questionPlaceholder')" />
      </label>

      <label class="fls-field">
        <span class="fls-field__label">{{ $t('propFaqBooking.faqSuggestions.answerLabel') }}</span>
        <textarea v-model="item.a" rows="2" class="fls-input fls-textarea" :placeholder="$t('propFaqBooking.faqSuggestions.answerPlaceholder')" />
      </label>

      <div v-if="item.siblings && item.siblings.length" class="fls-prop">
        <span class="fls-prop__label">{{ $t('propFaqBooking.faqSuggestions.applyToOthers') }}</span>
        <div class="fls-prop__chips">
          <button
            v-for="s in item.siblings"
            :key="s.id"
            type="button"
            class="fls-chip"
            :class="{ 'fls-chip--on': item.propagate.includes(s.id) }"
            @click="toggleSibling(item, s.id)"
          >
            <svg v-if="item.propagate.includes(s.id)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ s.name }}
          </button>
        </div>
      </div>

      <div class="fls-actions">
        <span v-if="item.error" class="fls-error">{{ item.error }}</span>
        <button type="button" class="fls-btn fls-btn--ghost" :disabled="item.busy" @click="dismiss(item)">{{ $t('propFaqBooking.faqSuggestions.dismiss') }}</button>
        <button type="button" class="fls-btn fls-btn--primary" :disabled="item.busy || !item.q.trim() || !item.a.trim()" @click="approve(item)">
          <span v-if="item.busy" class="fls-spin" />
          <span v-else>{{ $t('propFaqBooking.faqSuggestions.saveAsAnswer') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * Lista los candidatos de autoaprendizaje (preguntas que el agente escaló y el
 * anfitrión respondió manualmente) pendientes de revisión para una unidad.
 * Permite editar la pregunta/respuesta, propagar a unidades hermanas y aprobar
 * o descartar. Emite `learned` cuando se aprueba (el padre recarga las FAQ).
 */
export default {
  name: 'FaqLearningSuggestions',

  props: {
    propertyId: { type: [Number, String], default: null },
  },

  data() {
    return { items: [] };
  },

  computed: {
    // Sin propertyId → modo global (todos los pendientes del usuario): muestra
    // el nombre del alojamiento en cada tarjeta.
    global() {
      return !this.propertyId;
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      try {
        const params = {};
        if (this.propertyId) params.property_id = this.propertyId;
        const { data } = await axios.get('/agent-ia/learning-candidates', { params });
        const list = Array.isArray(data?.candidates) ? data.candidates : [];
        this.items = list.map((c) => ({
          id: c.id,
          question: c.question || '',
          property_name: c.property_name || '',
          q: c.question || '',
          a: c.answer || '',
          siblings: Array.isArray(c.siblings) ? c.siblings : [],
          propagate: [],
          busy: false,
          error: null,
        }));
      } catch (e) {
        // silencioso: si no hay candidatos o falla, no rompemos el panel
        this.items = [];
      }
    },

    toggleSibling(item, id) {
      const i = item.propagate.indexOf(id);
      if (i === -1) item.propagate.push(id);
      else item.propagate.splice(i, 1);
    },

    async approve(item) {
      if (!item.q.trim() || !item.a.trim()) return;
      this.$set(item, 'busy', true);
      item.error = null;
      try {
        await axios.post(`/agent-ia/learning-candidates/${item.id}/approve`, {
          q: item.q.trim(),
          a: item.a.trim(),
          propagate_property_ids: item.propagate,
        });
        this.items = this.items.filter((x) => x.id !== item.id);
        this.$emit('learned');
      } catch (e) {
        item.error = e?.response?.data?.message || this.$t('propFaqBooking.faqSuggestions.errors.saveFailed');
        this.$set(item, 'busy', false);
      }
    },

    async dismiss(item) {
      this.$set(item, 'busy', true);
      item.error = null;
      try {
        await axios.post(`/agent-ia/learning-candidates/${item.id}/dismiss`);
        this.items = this.items.filter((x) => x.id !== item.id);
      } catch (e) {
        item.error = e?.response?.data?.message || this.$t('propFaqBooking.faqSuggestions.errors.dismissFailed');
        this.$set(item, 'busy', false);
      }
    },
  },
};
</script>

<style scoped>
.fls { font-family: inherit; color: #1a1f2e; border: 1px solid #fde7c4; background: #fffaf2; border-radius: 16px; padding: 16px 18px; margin-bottom: 22px; }
.fls-head { display: flex; align-items: center; gap: 8px; color: #b45309; }
.fls-head__title { font-size: 15px; font-weight: 700; }
.fls-head__count { font-size: 11px; font-weight: 700; color: #fff; background: #f59e0b; border-radius: 999px; min-width: 20px; height: 20px; padding: 0 6px; display: inline-flex; align-items: center; justify-content: center; }
.fls-head__sub { color: #92702e; font-size: 13px; line-height: 1.5; margin: 6px 0 14px; }

.fls-card { background: #fff; border: 1px solid #f1e6d0; border-radius: 12px; padding: 14px; margin-bottom: 10px; }
.fls-card__prop { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 700; color: #6d28d9; background: #f3effe; border: 1px solid #e3d9fb; border-radius: 7px; padding: 3px 9px; margin-bottom: 10px; }
.fls-card__asked { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; background: #faf6ee; border-radius: 9px; margin-bottom: 10px; }
.fls-card__asked-label { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #b08545; }
.fls-card__asked-text { font-size: 14px; color: #4b3f29; }

.fls-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 9px; }
.fls-field__label { font-size: 12px; font-weight: 600; color: #6b7280; }
.fls-input { width: 100%; box-sizing: border-box; border: 1px solid #e3e6ee; border-radius: 10px; padding: 9px 12px; font-size: 14px; font-family: inherit; color: #1a1f2e; }
.fls-input:focus { outline: none; border-color: #f0b757; box-shadow: 0 0 0 3px #fdf0d8; }
.fls-textarea { resize: vertical; }

.fls-prop { margin: 8px 0 12px; }
.fls-prop__label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 6px; }
.fls-prop__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fls-chip { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #e3e6ee; background: #fff; color: #4b5563; border-radius: 999px; padding: 5px 11px; font-family: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.fls-chip--on { border-color: #f0b757; background: #fff7e8; color: #b45309; }

.fls-actions { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.fls-error { font-size: 12px; font-weight: 600; color: #dc2626; margin-right: auto; }
.fls-btn { border-radius: 10px; padding: 9px 16px; font-family: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; border: 1px solid transparent; display: inline-flex; align-items: center; justify-content: center; min-width: 96px; }
.fls-btn:disabled { opacity: .55; cursor: default; }
.fls-btn--ghost { background: #fff; border-color: #e5e7eb; color: #6b7280; }
.fls-btn--ghost:hover:not(:disabled) { background: #f9fafb; }
.fls-btn--primary { background: #f59e0b; color: #fff; }
.fls-btn--primary:hover:not(:disabled) { background: #e08c06; }
.fls-spin { width: 15px; height: 15px; border-radius: 50%; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; display: inline-block; animation: fls-spin .7s linear infinite; }
@keyframes fls-spin { to { transform: rotate(360deg); } }
</style>
